import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Volume2, X, Loader2, BookmarkCheck } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak } from "@/lib/buddy";

const HOLD_MS = 500;

const Card = ({ help, pos, onClose, voice }) => {
  const say = () => help.data && speak(`${help.data.word}. ${help.data.word}. ${help.data.meaning}`, voice, 0.75).catch(() => {});
  useEffect(() => { if (help.data) say(); }, [help.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const left = Math.max(12, Math.min(pos.x - 150, window.innerWidth - 312));
  const top = pos.y + 210 > window.innerHeight ? pos.y - 200 : pos.y + 14;
  return createPortal(
    <div className="fixed inset-0 z-[70]" onPointerDown={onClose} data-testid="word-help-backdrop">
      <div style={{ left, top }} onPointerDown={(e) => e.stopPropagation()} className="absolute w-[300px] rounded-3xl bg-[#FDFBF7] border border-[#E6DEC3] shadow-2xl p-5 rise" data-testid="word-help-card">
        <div className="flex items-start gap-2">
          <p className="font-display text-2xl text-[#2C4A3E] flex-1" data-testid="word-help-word">{help.data?.syllables || help.word}</p>
          {help.data && <button onClick={say} data-testid="word-help-say" aria-label="Say it again" className="p-2 rounded-full bg-[#F1EADB]"><Volume2 className="w-4 h-4" /></button>}
          <button onClick={onClose} data-testid="word-help-close" aria-label="Close" className="p-2 rounded-full hover:bg-[#F1EADB]"><X className="w-4 h-4" /></button>
        </div>
        {help.loading && <p className="text-sm text-stone-500 mt-2 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Looking it up…</p>}
        {help.error && <p className="text-sm text-[#C87D53] mt-2" data-testid="word-help-error">{help.error}</p>}
        {help.data && <>
          <p className="text-stone-700 mt-2" data-testid="word-help-meaning">{help.data.meaning}</p>
          {help.data.example && <p className="text-sm text-stone-500 italic mt-2" data-testid="word-help-example">“{help.data.example}”</p>}
          {help.data.saved && <Link to="/words" onClick={onClose} data-testid="word-help-saved" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#2C4A3E] hover:underline"><BookmarkCheck className="w-3.5 h-3.5" /> Saved to My Words</Link>}
        </>}
      </div>
    </div>, document.body);
};

export const WordHelp = ({ containerRef, onOpen }) => {
  const { buddy } = useProfile();
  const [help, setHelp] = useState(null);
  const t = useRef(null);
  const suppress = useRef(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const start = (e) => {
      const w = e.target.closest?.("[data-wi]");
      if (!w || !el.contains(w)) return;
      suppress.current = false;
      const pos = { x: e.clientX, y: e.clientY };
      t.current = setTimeout(async () => {
        suppress.current = true;
        onOpen?.();
        const word = w.textContent.trim();
        const context = (w.closest("p, li, h1, h2, h3, blockquote") || w.parentElement).textContent.slice(0, 500);
        setHelp({ word, pos, loading: true });
        try { const r = await http.post("/word-help", { word, context }); setHelp((h) => h && { ...h, loading: false, data: r.data }); }
        catch (err) { setHelp((h) => h && { ...h, loading: false, error: formatApiError(err) }); }
      }, HOLD_MS);
    };
    const cancel = () => clearTimeout(t.current);
    const move = (e) => { if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) cancel(); };
    const ends = ["pointerup", "pointerleave", "pointercancel"];
    const click = (e) => { if (suppress.current) { e.stopPropagation(); e.preventDefault(); suppress.current = false; } };
    const menu = (e) => { if (e.target.closest?.("[data-wi]")) e.preventDefault(); };
    el.addEventListener("pointerdown", start);
    ends.forEach((n) => el.addEventListener(n, cancel));
    el.addEventListener("pointermove", move);
    el.addEventListener("click", click, true);
    el.addEventListener("contextmenu", menu);
    return () => { cancel(); el.removeEventListener("pointerdown", start); el.removeEventListener("click", click, true); el.removeEventListener("contextmenu", menu); el.removeEventListener("pointermove", move); ends.forEach((n) => el.removeEventListener(n, cancel)); };
  }, [containerRef, onOpen]);
  if (!help) return null;
  return <Card help={help} pos={help.pos} voice={buddy?.voice} onClose={() => setHelp(null)} />;
};
